import { useApolloClient } from '@apollo/react-hooks';
import { getPmCategoryFromType } from '../components/paymentus/utils';
import {
  deleteProfile,
  getIFrameInfo,
  listProfiles,
} from '../queries/paymentus.query';
import {
  AccountCustomer,
  DeleteProfileRequest,
  IFrameInfoRequest,
  IFrameInfoResponse,
  ListProfilesResponse,
  PaymentCategory,
} from '../__generated__/pge-types';
import useAuthQuery from './useAuthQuery';
import usePgeQuery from './usePgeQuery';
import { useTranslation } from './useTranslation';

const { t } = useTranslation();

export const useSavedProfileList = (customer?: AccountCustomer) => {
  const { loading, error, data, refetch } = useAuthQuery<{
    listProfiles: ListProfilesResponse;
  }>(listProfiles, {
    variables: {
      payload: { loginId: customer?.personId || '-1' },
    },
    // need to fetch payment methods from server for out of band changes
    fetchPolicy: 'cache-and-network',
  });

  return {
    loading,
    error,
    refetch,
    savedProfileList: data?.listProfiles?.listProfilesResponse?.profile,
  };
};

export const useSSOToken = (props: IFrameInfoRequest) => {
  const query = props.ownerId ? useAuthQuery : usePgeQuery;
  console.log('props', props);
  return query<{ getIFrameInfo: IFrameInfoResponse }>(getIFrameInfo, {
    variables: {
      payload: {
        ...props,
        nickname: false,
      },
    },
  });
};

export const useDeleteProfile = () => {
  const apolloClient = useApolloClient();

  return {
    deleteProfile: (props: DeleteProfileRequest) => {
      return apolloClient.mutate<{
        deleteProfile: DeleteProfileRequest;
      }>({
        mutation: deleteProfile,
        variables: {
          payload: {
            ...props,
          },
        },
        errorPolicy: 'all',
      });
    },
  };
};

export const getPaymentCategoryLabel = (
  type?: string | null,
  addEndingSuffix?: boolean,
) => {
  const pmCategory = getPmCategoryFromType(type);

  switch (pmCategory) {
    case PaymentCategory.Cc:
      return `${t('CREDIT_CARD')}${
        addEndingSuffix ? ' ' + t('ENDING IN') : ''
      }`.toLowerCase();
    case PaymentCategory.Dc:
      return `${t('DEBIT_CARD')}${
        addEndingSuffix ? ' ' + t('ENDING IN') : ''
      }`.toLowerCase();
    case PaymentCategory.Dd:
      return `${t('BANK_ACCOUNT')}${
        addEndingSuffix ? ' ' + t('ENDING IN') : ''
      }`.toLowerCase();
    case PaymentCategory.PaypalAccount:
    case PaymentCategory.PaypalCredit:
    case PaymentCategory.Ap:
    case PaymentCategory.AmazonPay:
    case 'VENMO':
      return `${t(pmCategory.toString())} ${t('ACCOUNT').toLowerCase()}`;
    default:
      return t('Payment method');
  }
};

export const getPaymentMethodLabelForType = (type?: string | null) => {
  return type ? t(`PAYMENTUS_PM_CATEGORY_${type}`) : '';
};

export const getPaymentMethodLabel = (
  type?: string | null,
  makeLowercase?: boolean,
) => {
  const methodLabel = getPaymentMethodLabelForType(type);

  const pmCategory = getPmCategoryFromType(type);

  switch (pmCategory) {
    case PaymentCategory.Cc:
    case PaymentCategory.Dc:
    case PaymentCategory.Dd:
      return makeLowercase ? methodLabel.toLowerCase() : methodLabel;
    case PaymentCategory.PaypalAccount:
    case PaymentCategory.PaypalCredit:
    case PaymentCategory.Ap:    
    case PaymentCategory.Venmo:
      return `${methodLabel} ${t('ACCOUNT').toLowerCase()}`;
    default:
      return t('');
  }
};

// TMP HACK - method to force refetch the list of payment
// methods - used when adding paypal as we don't get
// cardholder name back when adding the method in the iframe
export const useRefetchListProfiles = (loginId: string = '-1') => {
  const { refetch } = useAuthQuery<{
    listProfiles: ListProfilesResponse;
  }>(listProfiles, {
    variables: {
      payload: { loginId },
    },
    // don't update cache to avoid any surprises in other code flows
    fetchPolicy: 'no-cache',
  });

  return {
    refetch,
  };
};
